/**
 * Phase 4 — Safety Dashboard. Severity counts + time-to-close + two tabs
 * (Observations / Near-Miss) with filters and click-through to detail pages.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, Plus, Clock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const SEV_TONE = {
  low: 'bg-slate-100 text-slate-700 border-slate-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};
const STATUS_TONE = {
  open: 'bg-red-50 text-red-700 border-red-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  investigating: 'bg-amber-50 text-amber-700 border-amber-200',
  actions_pending: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  waived: 'bg-slate-100 text-slate-600 border-slate-200',
};
import { formatDate as fmt } from '../../lib/format';
const SEVERITIES = ['low', 'medium', 'high', 'critical'];

const SevCounts = ({ title, data, icon: Icon }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4">
    <div className="kpi-overline mb-3 flex items-center gap-2"><Icon size={12} /> {title}</div>
    <div className="grid grid-cols-4 gap-2">
      {SEVERITIES.map((s) => (
        <div key={s} className={`rounded-sm border p-2 text-center ${SEV_TONE[s]}`}>
          <div className="text-xl font-display font-extrabold tabular-nums">{data?.[s] || 0}</div>
          <div className="text-[9px] uppercase tracking-wider font-bold">{s}</div>
        </div>
      ))}
    </div>
  </div>
);

export default function SafetyDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useUrlTab('tab', 'observations');
  const [obs, setObs] = useState([]);
  const [nm, setNm] = useState([]);
  const [fSeverity, setFSeverity] = useState('');
  const [fStatus, setFStatus] = useState('');

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);

  const projQ = projectId ? `project_id=${projectId}&` : '';
  useEffect(() => {
    apiGet(`/safety/dashboard?${projQ}`).then(setDash).catch(() => {});
  }, [projectId]); // eslint-disable-line

  useEffect(() => {
    const q = `${projQ}${fSeverity ? `severity=${fSeverity}&` : ''}${fStatus ? `status=${fStatus}&` : ''}`;
    if (tab === 'observations') {
      apiGet(`/safety/observations?${q}`).then((d) => setObs(d.observations || [])).catch(() => {});
    } else {
      apiGet(`/safety/near-miss?${q}`).then((d) => setNm(d.near_misses || [])).catch(() => {});
    }
  }, [tab, projectId, fSeverity, fStatus]); // eslint-disable-line

  const ttc = dash?.observations?.avg_time_to_close_days || {};
  const factors = dash?.top_contributing_factors || [];
  const statusOptions = useMemo(() => (tab === 'observations'
    ? ['open', 'in_progress', 'closed', 'waived']
    : ['open', 'investigating', 'actions_pending', 'closed']), [tab]);

  return (
    <div>
      <PageHeader overline="Safety / HSE" title="Safety Dashboard" subtitle="Observations, near-misses and how fast we close the loop"
        actions={(
          <div className="flex gap-2">
            <button onClick={() => navigate('/app/safety/raise-observation')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="dash-raise-obs"><Plus size={14} /> Observation</button>
            <button onClick={() => navigate('/app/safety/raise-near-miss')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50" data-testid="dash-raise-nm"><Plus size={14} /> Near-Miss</button>
          </div>
        )}
      />

      <div className="mb-4">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="safety-project-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[260px]">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <SevCounts title="Open Observations" data={dash?.observations?.open_by_severity} icon={ShieldAlert} />
        <SevCounts title="Open Near-Misses" data={dash?.near_misses?.open_by_severity} icon={AlertTriangle} />
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline mb-3 flex items-center gap-2"><Clock size={12} /> Avg Time to Close (days)</div>
          <div className="grid grid-cols-4 gap-2">
            {SEVERITIES.map((s) => (
              <div key={s} className="rounded-sm border border-slate-200 p-2 text-center">
                <div className="text-xl font-display font-extrabold tabular-nums text-slate-700">{ttc[s] != null ? ttc[s] : '—'}</div>
                <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400">{s}</div>
              </div>
            ))}
          </div>
          {factors.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Top contributing factors</div>
              <div className="flex flex-wrap gap-1">
                {factors.map((f, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{f.factor} ({f.count})</span>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-3">
        {[['observations', 'Observations'], ['near_miss', 'Near-Miss']].map(([v, l]) => (
          <button key={v} onClick={() => { setTab(v); setFStatus(''); }} data-testid={`safety-tab-${v}`}
            className={`px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px transition-colors ${tab === v ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={fSeverity} onChange={(e) => setFSeverity(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="safety-filter-severity">
          <option value="">All severities</option>
          {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="safety-filter-status">
          <option value="">All statuses</option>
          {statusOptions.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="safety-list-table">
            {tab === 'observations' ? (
              <>
                <thead><tr><th>Observation</th><th>Category</th><th>Severity</th><th>Raised</th><th>Status</th></tr></thead>
                <tbody>
                  {obs.length === 0 ? <tr><td colSpan={5} className="text-center text-sm text-slate-400 py-10">No observations.</td></tr>
                    : obs.map((o) => (
                      <tr key={o.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/safety/observations/${o.id}`)} data-testid={`obs-row-${o.id}`}>
                        <td className="text-[13px] font-display font-semibold text-[#0F172A] max-w-[280px] truncate">{o.description}</td>
                        <td className="text-xs text-slate-600">{o.category}</td>
                        <td><span className={`tag border ${SEV_TONE[o.severity]}`}>{o.severity}</span></td>
                        <td className="text-xs text-slate-500">{fmt(o.raised_at)}</td>
                        <td><span className={`tag border ${STATUS_TONE[o.status]}`}>{(o.status || '').replace('_', ' ')}</span></td>
                      </tr>
                    ))}
                </tbody>
              </>
            ) : (
              <>
                <thead><tr><th>Near-Miss</th><th>Potential</th><th>Raised</th><th>Status</th></tr></thead>
                <tbody>
                  {nm.length === 0 ? <tr><td colSpan={4} className="text-center text-sm text-slate-400 py-10">No near-misses.</td></tr>
                    : nm.map((n) => (
                      <tr key={n.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/safety/near-miss/${n.id}`)} data-testid={`nm-row-${n.id}`}>
                        <td className="text-[13px] font-display font-semibold text-[#0F172A] max-w-[320px] truncate">{n.description}</td>
                        <td><span className={`tag border ${SEV_TONE[n.potential_severity]}`}>{n.potential_severity}</span></td>
                        <td className="text-xs text-slate-500">{fmt(n.raised_at)}</td>
                        <td><span className={`tag border ${STATUS_TONE[n.status]}`}>{(n.status || '').replace('_', ' ')}</span></td>
                      </tr>
                    ))}
                </tbody>
              </>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
